// /combos/new — Create Combo (PAGES.md §3.96)
//
// Create a combo/meal deal: select items, set price, configure availability.
// RESTAURANT only — OWNER+ (50). Traceability: FR-ORD-047, FR-ORD-048

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Input,
  Select,
  Toggle,
  TimePicker,
  DatePicker,
  Alert,
  ErrorBoundary,
  Modal,
  StatCard,
  FormField,
  FileUpload,
  DataTable,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { comboService, DAY_LABELS, comboDateToApi } from '@/services/combo.service';
import type { SelectedItem, ProductMinimal } from '@/services/combo.service';
import { productService } from '@/services/product.service';
import { locationService } from '@/services/location.service';
import { useDashboardStore } from '@/stores/use-dashboard-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';
import { PlusIcon, TrashIcon, PackageIcon, ImageIcon } from './icons';

// Sentinel for the "All Locations" choice — creates the combo at every outlet.
const ALL_LOCATIONS = '__ALL__';

// ─── Component ──────────────────────────────────────────────────────────────

function ComboCreateContent() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const selectedLocationId = useDashboardStore((s) => s.selectedLocationId);

  const [name, setName] = useState('');
  const [priceDisplay, setPriceDisplay] = useState('');
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [alwaysAvailable, setAlwaysAvailable] = useState(true);
  const [availStart, setAvailStart] = useState('12:00');
  const [availEnd, setAvailEnd] = useState('15:00');
  const [availDays, setAvailDays] = useState<number[]>([1, 2, 3, 4, 5]);
  // Validity window — calendar dates, independent of the time-of-day window.
  const [validFrom, setValidFrom] = useState<Date | undefined>(undefined);
  const [validUntil, setValidUntil] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Combos are created per-location. Default to the header-selected location
  // when one is chosen; when the header is "All Locations" (null) the cashier
  // must pick a specific outlet here before the combo can be created.
  const [formLocationId, setFormLocationId] = useState<string>(selectedLocationId ?? '');
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    locationService
      .list({ is_active: 'true', limit: 100 })
      .then((res) => {
        const locs = (res.data ?? []).map((l) => ({ value: l.id, label: l.name }));
        // Offer "All Locations" (creates the combo at every outlet) when there's more than one.
        const opts = locs.length > 1 ? [{ value: ALL_LOCATIONS, label: 'All Locations' }, ...locs] : locs;
        setLocationOptions(opts);
        // Pre-select the header location, or the only location if there's just one.
        setFormLocationId((cur) => cur || selectedLocationId || (locs.length === 1 ? locs[0].value : ''));
      })
      .catch(() => { /* silent — the field stays empty and blocks save with a clear error */ });
  }, [selectedLocationId]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [products, setProducts] = useState<ProductMinimal[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const comboPricePaise = Math.round(parseFloat(priceDisplay || '0') * getSubunitMultiplier(currency));
  const individualTotal = useMemo(() => items.reduce((s, i) => s + i.quantity * i.individual_price, 0), [items]);
  const savingsPercent = individualTotal > 0
    ? Math.round(((individualTotal - comboPricePaise) / individualTotal) * 10000) / 100
    : 0;
  const priceExceedsTotal = comboPricePaise > 0 && individualTotal > 0 && comboPricePaise >= individualTotal;

  const fetchProducts = useCallback(async (query?: string) => {
    try {
      const result = await productService.list({ location_id: (formLocationId && formLocationId !== ALL_LOCATIONS) ? formLocationId : undefined, search: query || undefined, limit: 200 });
      setProducts((result.data as unknown as ProductMinimal[]).map((p) => ({ id: p.id, name: p.name, price: (p as unknown as { price: number }).price ?? 0 })));
    } catch { /* silent */ }
  }, [formLocationId]);

  const openPicker = useCallback(() => {
    setProductSearch('');
    setPickerOpen(true);
    fetchProducts();
  }, [fetchProducts]);

  const handleProductSearch = useCallback((val: string) => {
    setProductSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchProducts(val), 300);
  }, [fetchProducts]);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const addProduct = useCallback((p: ProductMinimal) => {
    if (items.some((i) => i.product_id === p.id)) return;
    setItems((prev) => [...prev, { id: `sel-${Date.now()}`, product_id: p.id, product_name: p.name, quantity: 1, individual_price: p.price }]);
    setPickerOpen(false);
    setFormError(null);
  }, [items]);

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, qty: number) => {
    setItems((prev) => prev.map((i) => (i.product_id === productId ? { ...i, quantity: Math.max(1, qty) } : i)));
  }, []);

  const toggleDay = useCallback((day: number) => {
    setAvailDays((prev) => prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort());
  }, []);

  const handleImageUpload = useCallback((files: File[]) => {
    if (files.length === 0) { setImagePreview(null); return; }
    const url = URL.createObjectURL(files[0]);
    setImagePreview(url);
  }, []);

  const validate = useCallback((): boolean => {
    if (!name.trim()) { setFormError('Combo name is required'); return false; }
    if (!formLocationId) { setFormError('Select a location for this combo'); return false; }
    if (items.length < 2) { setFormError('Add at least 2 items to create a combo'); return false; }
    if (comboPricePaise <= 0) { setFormError('Combo price must be greater than zero'); return false; }
    if (priceExceedsTotal) { setFormError(`Combo price must be less than individual total (${formatPaise(individualTotal, currency)})`); return false; }
    if (validFrom && validUntil && validUntil < validFrom) { setFormError('Valid Until must be on or after Valid From'); return false; }
    setFormError(null);
    return true;
  }, [name, formLocationId, items, comboPricePaise, priceExceedsTotal, individualTotal, currency, validFrom, validUntil]);

  const handleCreate = useCallback(async () => {
    if (!validate()) return;
    setIsSaving(true);
    const base = {
      name: name.trim(),
      combo_price: comboPricePaise,
      items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      is_active: isActive,
      availability_start: alwaysAvailable ? undefined : availStart,
      availability_end: alwaysAvailable ? undefined : availEnd,
      availability_days: alwaysAvailable ? undefined : availDays,
      valid_from: comboDateToApi(validFrom),
      valid_until: comboDateToApi(validUntil),
    };
    try {
      if (formLocationId === ALL_LOCATIONS) {
        // Create the combo once per outlet. Names are unique per (tenant,
        // location), so the same combo can live in every location; outlets
        // where it already exists are reported as skipped rather than failing all.
        const ids = locationOptions.filter((o) => o.value && o.value !== ALL_LOCATIONS).map((o) => o.value);
        const results = await Promise.allSettled(ids.map((id) => comboService.create({ ...base, location_id: id })));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const skipped = results.length - ok;
        if (ok === 0) { setFormError('Could not create the combo in any location (it may already exist).'); return; }
        toast({
          title: `"${name.trim()}" created in ${ok} location${ok > 1 ? 's' : ''}${skipped ? ` — ${skipped} skipped` : ''}`,
          variant: skipped ? 'warning' : 'success',
        });
        navigate('/combos');
        return;
      }
      await comboService.create({ ...base, location_id: formLocationId });
      toast({ title: `"${name.trim()}" created`, variant: 'success' });
      navigate('/combos');
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.message.includes('COMBO_NAME_DUPLICATE')) setFormError('A combo with this name already exists');
        else if (err.message.includes('COMBO_PRICE_EXCEEDS_TOTAL')) setFormError('Combo price must be less than individual total');
        else toast({ title: err.message, variant: 'error' });
      } else {
        toast({ title: 'Failed to create combo', variant: 'error' });
      }
    } finally {
      setIsSaving(false);
    }
  }, [validate, formLocationId, locationOptions, name, comboPricePaise, items, isActive, alwaysAvailable, availStart, availEnd, availDays, validFrom, validUntil, navigate, toast]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); handleCreate(); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase();
    return products.filter((p) => !items.some((i) => i.product_id === p.id) && p.name.toLowerCase().includes(q));
  }, [products, items, productSearch]);

  const itemColumns: ColumnDef<SelectedItem>[] = useMemo(() => [
    { key: 'product_name', header: 'Item', render: (_v: unknown, r: SelectedItem) => <span className="font-semibold text-neutral-800 dark:text-neutral-200">{r.product_name}</span> },
    { key: 'quantity', header: 'Qty', numeric: true, render: (_v: unknown, r: SelectedItem) => (
      <input type="number" value={r.quantity} onChange={(e) => updateQuantity(r.product_id, Number(e.target.value) || 1)} min="1"
        className="w-16 px-2 py-1 rounded-lg bg-neutral-50 dark:bg-neutral-700 text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 focus:ring-2 focus:ring-violet-500 focus:outline-none" aria-label={`Quantity for ${r.product_name}`} />
    )},
    { key: 'individual_price', header: 'Unit Price', numeric: true, render: (_v: unknown, r: SelectedItem) => <span className="font-mono tabular-nums text-neutral-600 dark:text-neutral-400">{formatPaise(r.individual_price, currency)}</span> },
    { key: 'line_total', header: 'Subtotal', numeric: true, render: (_v: unknown, r: SelectedItem) => <span className="font-mono tabular-nums font-bold">{formatPaise(r.quantity * r.individual_price, currency)}</span> },
    { key: 'actions', header: '', render: (_v: unknown, r: SelectedItem) => (
      <Button variant="ghost" size="sm" onClick={() => removeItem(r.product_id)} className="text-danger-600 hover:text-danger-700 hover:bg-danger-50 dark:hover:bg-danger-900/20" aria-label={`Remove ${r.product_name}`}><TrashIcon /></Button>
    )},
  ], [updateQuantity, removeItem, currency]);

  return (
    <PageContainer title="New Combo" breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Combos', href: '/combos' }, { label: 'New Combo' }]}
      actions={<Button variant="primary" size="sm" onClick={handleCreate} disabled={isSaving}>{isSaving ? 'Creating...' : 'Create Combo'}</Button>}>
      <div className="animate-fade-in space-y-6">
        {/* KPI Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="animate-slide-up">
            <StatCard title="Individual Total" value={formatPaise(individualTotal, currency)} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard title="Combo Price" value={comboPricePaise > 0 ? formatPaise(comboPricePaise, currency) : '—'} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard title="Savings" value={savingsPercent > 0 ? `${savingsPercent.toFixed(1)}%` : '—'} icon={<PackageIcon className="h-4 w-4" />} />
          </div>
        </div>

        {formError && <Alert variant="danger" description={formError} dismissible onDismiss={() => setFormError(null)} />}
        {priceExceedsTotal && !formError && <Alert variant="warning" description={`Combo price must be less than individual total (${formatPaise(individualTotal, currency)})`} />}

        {/* Basic Info — relative z-10 keeps the Location dropdown above the
            sibling glass cards below (which create their own stacking context). */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4 relative z-10')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Basic Info</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Combo Name" required>
              <Input value={name} onChange={(val) => { setName(val); setFormError(null); }} placeholder="e.g. Lunch Combo, Family Pack" aria-label="Combo name" />
            </FormField>
            <FormField label={`Combo Price (${getCurrencySymbol(currency)})`} required>
              <Input value={priceDisplay} onChange={(val) => { setPriceDisplay(val); setFormError(null); }} type="number" placeholder="299.00" aria-label="Combo price in rupees" />
            </FormField>
          </div>
          <FormField label="Location" required>
            <Select
              options={locationOptions}
              value={formLocationId}
              onChange={(v) => { setFormLocationId(typeof v === 'string' ? v : (v[0] ?? '')); setFormError(null); }}
              placeholder="Select a location"
              aria-label="Combo location"
            />
          </FormField>
          <div className="flex items-center gap-3">
            <Toggle checked={isActive} onChange={setIsActive} aria-label="Active status" />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>

        {/* Combo Image */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider flex items-center gap-2">
            <ImageIcon /> Combo Image
          </h2>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">Optional — displayed on the POS menu browser</p>
          <FileUpload
            onUpload={handleImageUpload}
            accept="image/png,image/jpeg,image/webp"
            maxSize={5 * 1024 * 1024}
            preview={imagePreview}
            variant="dropzone"
          />
        </div>

        {/* Items */}
        <div className={cn(GLASS, 'overflow-hidden')}>
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 dark:border-neutral-700/50">
            <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Items ({items.length})</h2>
            <Button variant="secondary" size="sm" onClick={openPicker}><PlusIcon /><span className="ml-1.5">Add Item</span></Button>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center"><p className="text-sm text-neutral-500 dark:text-neutral-400">Add at least 2 items to create a combo</p></div>
          ) : (
            <div className="overflow-x-auto"><DataTable<SelectedItem> columns={itemColumns} data={items} /></div>
          )}
        </div>

        {/* Availability */}
        <div className={cn(GLASS_FORM, 'p-5 space-y-4')}>
          <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Availability</h2>

          {/* Validity date window — seasonal combos run only between these dates.
              Independent of the time-of-day window below. Leave blank for no bound. */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Valid From">
              <DatePicker value={validFrom} onChange={setValidFrom} maxDate={validUntil} placeholder="No start date" />
            </FormField>
            <FormField label="Valid Until">
              <DatePicker value={validUntil} onChange={setValidUntil} minDate={validFrom} placeholder="No end date" />
            </FormField>
          </div>

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100 dark:border-neutral-700/50">
            <Toggle checked={alwaysAvailable} onChange={setAlwaysAvailable} aria-label="Always available" />
            <span className="text-sm text-neutral-600 dark:text-neutral-400">{alwaysAvailable ? 'Always available' : 'Time-based'}</span>
          </div>
          {!alwaysAvailable && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Start Time">
                  <TimePicker value={availStart} onChange={setAvailStart} />
                </FormField>
                <FormField label="End Time">
                  <TimePicker value={availEnd} onChange={setAvailEnd} />
                </FormField>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Days</label>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Available days">
                  {DAY_LABELS.map((label, idx) => (
                    <button key={idx} type="button" onClick={() => toggleDay(idx)}
                      aria-pressed={availDays.includes(idx)}
                      className={cn(
                        'px-3 py-1.5 rounded-full text-xs font-bold transition-all',
                        availDays.includes(idx)
                          ? 'bg-violet-600 text-white shadow-md'
                          : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300',
                      )}
                      aria-label={`Toggle ${label}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title="Add Item" variant="wide">
        <div className="space-y-4 p-1">
          <Input value={productSearch} onChange={handleProductSearch} placeholder="Search products..." aria-label="Search products" />
          <div className="max-h-64 overflow-y-auto space-y-1" role="listbox" aria-label="Product list">
            {filteredProducts.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">No products found</p>
            ) : filteredProducts.map((p) => (
              <button key={p.id} type="button" role="option" aria-selected={false} onClick={() => addProduct(p)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors text-left">
                <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">{p.name}</span>
                <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">{formatPaise(p.price, currency)}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function ComboCreatePage() {
  return <ErrorBoundary><ComboCreateContent /></ErrorBoundary>;
}
